import React, { useContext, useEffect, useMemo, useState } from 'react';

import { Skeleton } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';

import BdsSegment from '../../../../../shared/components/v3/BdsSegment/BdsSegment';
import Card from '../../../../../shared/components/v3/Cards/Card/Card';
import EmptyCard from '../../../../../shared/components/v3/Cards/EmptyCard/EmptyCard';
import ErrorCard from '../../../../../shared/components/v3/Cards/ErrorCard/ErrorCard';
import StackedCirclesChart from '../../../../../shared/components/v3/Charts/StackedCirclesChart/StackedCirclesChart';
import {
  CLICK_ACTION,
  HOME_RF_TABS_DICT,
  SECTION_BALANCE_SUMMARY,
  SELECT_TAB_RF
} from '../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { PlansContext } from '../../../../../shared/context/plans/plansContext';
import { useRFCompanyConsumption } from '../../../../../shared/hooks/useRFCompanyConsumption/useRFCompanyConsumption';
import { IFlexibleRemunerationPayRollCycle } from '../../../../../shared/interfaces/company';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_GLOBAL } from '../../../../../shared/translations/commons';
import { TRANSLATIONS_HOME } from '../../../../../shared/translations/home';
import { getLocalizedMonthName, toLocaleNumberFormat, toTitleCase } from '../../../../../shared/utils/utils';
import './MetricsCard.scss';

const MetricsCard = () => {
  const [tabIndex, setTabIndex] = useState(1);
  const [selectedCycle, setSelectedCycle] = useState<IFlexibleRemunerationPayRollCycle | undefined>();
  const { companyRFCycles, isLoadingRFCycles, errorGettingRFCycles, isLoadingCompanyData, fetchCompanyCycles } =
    useContext(CompanyContext);
  const { modelType } = useContext(PlansContext);
  const { t } = useTranslation();

  const { consumptionData, error, loading, refetch } = useRFCompanyConsumption(selectedCycle, !!tabIndex);

  const title = t(TRANSLATIONS_HOME.expensesSavingsCard.title);

  const tabsItems = [
    t(TRANSLATIONS_HOME.expensesSavingsCard.lastCycle),
    t(TRANSLATIONS_HOME.expensesSavingsCard.currentCycle)
  ];

  const handleTabChange = (index: number) => {
    setTabIndex(index);
    setSelectedCycle(index === 0 ? companyRFCycles?.payRollCycles[1] : companyRFCycles?.payRollCycles[0]);
    TagManagerService.pushEvent(CLICK_ACTION, SELECT_TAB_RF, {
      ...SECTION_BALANCE_SUMMARY,
      ...HOME_RF_TABS_DICT[index]
    });
  };

  useEffect(() => {
    if (companyRFCycles) {
      setSelectedCycle(companyRFCycles.payRollCycles[0]);
    } else {
      setSelectedCycle(undefined);
    }
  }, [companyRFCycles]);

  const handleHeaderCycle = () => {
    if (errorGettingRFCycles || !companyRFCycles || !selectedCycle) {
      return;
    }
    if (companyRFCycles.payRollCycles.length === 1) {
      const month = toTitleCase(getLocalizedMonthName(selectedCycle.cycleMonth));
      return (
        <div>
          <Trans
            i18nKey={TRANSLATIONS_HOME.expensesCard.onlyCycle}
            components={{
              b: <b></b>
            }}
            values={{
              month,
              year: selectedCycle.cycleYear
            }}
          />
        </div>
      );
    }
    return <BdsSegment items={tabsItems} activeIndex={tabIndex} action={handleTabChange} type="secondary" size="sm" />;
  };

  const isLoading = useMemo(() => {
    return loading || isLoadingRFCycles || isLoadingCompanyData;
  }, [loading, isLoadingRFCycles, isLoadingCompanyData]);

  const isEmptyState = useMemo(() => {
    return !consumptionData?.totalBalance || !consumptionData?.currency;
  }, [consumptionData]);

  const chartData = useMemo(() => {
    if (consumptionData) {
      return [
        {
          value: consumptionData.totalBalance,
          label: t(TRANSLATIONS_HOME.expensesSavingsCard.expenses),
          tooltip: t(
            modelType === 'forecast'
              ? TRANSLATIONS_HOME.expensesSavingsCard.tooltip.forecast.expenses
              : TRANSLATIONS_HOME.expensesSavingsCard.tooltip.expenses,
            {
              value: toLocaleNumberFormat(
                consumptionData.totalBalance,
                { style: 'currency', currency: consumptionData.currency, useGrouping: true },
                'es-ES'
              )
            }
          )
        },
        {
          value: consumptionData.totalSavedBalance,
          label: t(TRANSLATIONS_HOME.expensesSavingsCard.savings),
          tooltip: t(
            modelType === 'forecast'
              ? TRANSLATIONS_HOME.expensesSavingsCard.tooltip.forecast.savings
              : TRANSLATIONS_HOME.expensesSavingsCard.tooltip.savings,
            {
              value: `${toLocaleNumberFormat(
                consumptionData.totalSavedBalance,
                { style: 'currency', currency: consumptionData.currency, useGrouping: true },
                'es-ES'
              )}`
            }
          )
        }
      ];
    }
    return [];
  }, [consumptionData, t]);

  if (isLoading) {
    return (
      <div className="rf-home-metrics-skeleton-container">
        <Skeleton className="rf-home-metrics-skeleton" variant="rectangular" />
      </div>
    );
  }

  if (errorGettingRFCycles) {
    return (
      <Card className="rf-home-metrics-states" title={title} headButton={handleHeaderCycle()}>
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={fetchCompanyCycles}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          className="rf-home-metrics-error"
        />
      </Card>
    );
  }

  if (error && selectedCycle) {
    return (
      <Card className="rf-home-metrics-states" title={title} headButton={handleHeaderCycle()}>
        <ErrorCard
          primaryText={t(TRANSLATIONS_GLOBAL.errorCard.title)}
          secondaryText={t(TRANSLATIONS_GLOBAL.errorCard.description)}
          action={() => refetch(selectedCycle, !!tabIndex)}
          buttonText={t(TRANSLATIONS_GLOBAL.errorCard.buttonText)}
          className="rf-home-metrics-error"
        />
      </Card>
    );
  }

  if (isEmptyState || !consumptionData) {
    return (
      <Card className="rf-home-metrics-states" title={title} headButton={handleHeaderCycle()}>
        <EmptyCard
          primaryText={t(TRANSLATIONS_HOME.expensesSavingsCard.emptyState.emptyStateTitle)}
          secondaryText={t(TRANSLATIONS_HOME.expensesSavingsCard.emptyState.emptyStateDescription)}
          className="rf-home-metrics-empty"
        />
      </Card>
    );
  }

  return (
    <Card
      className="rf-home-metrics-card-container"
      title={title}
      data-experience="new"
      headButton={handleHeaderCycle()}
    >
      <div className="rf-home-metrics-card-container">
        <div className="rf-home-metrics-card-text-container">
          <div className="flex flex-col gap-2">
            <div className="number color-0">
              {toLocaleNumberFormat(
                consumptionData.totalBalance,
                { style: 'currency', currency: consumptionData.currency, useGrouping: true },
                'es-ES'
              )}
            </div>
            <div className="label">
              {t(
                modelType === 'forecast'
                  ? TRANSLATIONS_HOME.expensesSavingsCard.forecastedConsumption
                  : TRANSLATIONS_HOME.expensesSavingsCard.totalExpenses
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <div className="number color-1">
              {toLocaleNumberFormat(
                consumptionData.totalSavedBalance,
                { style: 'currency', currency: consumptionData.currency, useGrouping: true },
                'es-ES'
              )}
            </div>
            <div className="label">
              {t(
                modelType === 'forecast'
                  ? TRANSLATIONS_HOME.expensesSavingsCard.forecastedSavings
                  : TRANSLATIONS_HOME.expensesSavingsCard.totalSavings
              )}
            </div>
          </div>
        </div>
        <StackedCirclesChart data={chartData} colorScheme={'home-rf'} height={190} />
      </div>
    </Card>
  );
};

export default MetricsCard;
